// @mui
import { useBillingDetails } from 'api/apiHooks';
import PartialPageSpinner from 'components/PartialPageSpinner';
import {
  Grid,
  Card,
  Table,
  Typography,
  Tooltip,
  Stack,
  TableBody,
  TableContainer,
  IconButton,
  Paper,
} from '@mui/material';
import {
  TableEmptyRows,
  TableHeadCustom,
  TableNoData,
  TableSelectedActions,
} from 'components/table';
import useTable, { emptyRows } from 'hooks/useTable';
import usePortalId from 'hooks/portal/usePortalId';
import { fDate } from 'utils/formatTime';
import AccountBillingTableRow from 'sections/user/accounts/AccountBiilingTableRow';
import { BILLING_CANCEL_SUBSCRIPTION_API_URL } from 'api/apiConstants';
import { useSnackbar } from 'notistack';
import axiosInstance from 'utils/axios';
import { useNavigate } from 'react-router-dom';
import Iconify from 'components/Iconify';
import { LoadingButton } from '@mui/lab';
import { useState } from 'react';
import { PATH_USER } from 'routes/paths';
import { useTranslation } from 'react-i18next';

export default function AccountBilling() {
  const {
    dense,
    page,
    order,
    orderBy,
    rowsPerPage,
    selected,
    onSelectAllRows,
    onSort,
  } = useTable({
    defaultOrderBy: 'createdAt',
  });
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const portalId = usePortalId();
  const { data } = useBillingDetails(Number(portalId));
  const [loading, setLoading] = useState(false);
  const { t } = useTranslation();

  const TABLE_HEAD = [
    { id: 'label', label: t('date'), align: 'left' },
    { id: 'amount', label: t('amount'), align: 'left' },
    { id: 'download_invoice', label: t('download_invoice'), align: 'center' },
  ];

  if (data === undefined) {
    return <PartialPageSpinner />;
  }
  const billingDetails = data.data;
  const paymentsList = billingDetails.payments_list;
  const isNotFound = !paymentsList.length;

  const denseHeight = dense ? 60 : 80;

  const cancelSubscription = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.delete(
        `${BILLING_CANCEL_SUBSCRIPTION_API_URL}?portal_id=${portalId}`
      );
      enqueueSnackbar('Subscription Cancelled', { variant: 'success' });
      navigate(PATH_USER.general.accountSettings(portalId, 'billing'));
    } catch (error: any) {
      enqueueSnackbar(error.data, { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const todayDate = new Date();
  const today =
    todayDate.getFullYear() +
    '-' +
    (todayDate.getMonth() + 1) +
    '-' +
    todayDate.getDate();
  const validityDate = new Date(Number(billingDetails.active_plan.validity));
  const validity =
    validityDate.getFullYear() +
    '-' +
    (validityDate.getMonth() + 1) +
    '-' +
    validityDate.getDate();

  let expire = null;
  if (today === validity) {
    expire = t('will_expire_today');
  } else {
    expire =
      t('will_expire_on') +
      ' ' +
      fDate(Number(billingDetails.active_plan.validity));
  }

  return (
    <Grid container spacing={5}>
      <Grid item xs={12} md={8}>
        <Stack spacing={2}>
          <Card sx={{ p: 3 }}>
            <Typography
              variant='overline'
              sx={{ mb: 3, display: 'block', color: 'text.secondary' }}
            >
              {t('your_plan')}
            </Typography>
            <Typography variant='overline'>
              {billingDetails.active_plan.plan_type}
            </Typography>
            <Typography
              sx={{
                mb: 3,
                fontSize: '15px',
                display: 'block',
                color: 'text.secondary',
              }}
              variant='caption'
            >
              {expire}
            </Typography>
          </Card>
          <Card sx={{ p: 3 }}>
            <Typography
              variant='overline'
              sx={{ mb: 3, display: 'block', color: 'text.secondary' }}
            >
              {t('invoice_history')}
            </Typography>
            <TableContainer component={Paper}>
              {selected.length > 0 && (
                <TableSelectedActions
                  dense={dense}
                  numSelected={selected.length}
                  rowCount={paymentsList.length}
                  onSelectAllRows={checked =>
                    onSelectAllRows(
                      checked,
                      paymentsList.map(billing => billing._id)
                    )
                  }
                  actions={
                    <Tooltip title='Delete'>
                      <IconButton color='primary' onClick={() => {}}>
                        <Iconify icon={'eva:trash-2-outline'} />
                      </IconButton>
                    </Tooltip>
                  }
                />
              )}
              <Table size={dense ? 'small' : 'medium'}>
                <TableHeadCustom
                  order={order}
                  orderBy={orderBy}
                  headLabel={TABLE_HEAD}
                  rowCount={paymentsList.length}
                  numSelected={selected.length}
                  onSort={onSort}
                  onSelectAllRows={checked =>
                    onSelectAllRows(
                      checked,
                      paymentsList.map(billing => billing._id)
                    )
                  }
                />
                <TableBody>
                  {paymentsList.length
                    ? paymentsList.map((billing, index) => {
                        return (
                          <AccountBillingTableRow
                            key={billing._id}
                            row={billing}
                          />
                        );
                      })
                    : null}
                  <TableEmptyRows
                    height={denseHeight}
                    emptyRows={emptyRows(
                      page,
                      rowsPerPage,
                      paymentsList.length
                    )}
                  />
                  <TableNoData isNotFound={isNotFound} Message={'Invoice'} />
                </TableBody>
              </Table>
            </TableContainer>
          </Card>
          {billingDetails.is_subscribed ? (
            <LoadingButton
              onClick={cancelSubscription}
              variant='contained'
              color='error'
              loading={loading}
            >
              {t('cancel_subscription')}
            </LoadingButton>
          ) : null}
        </Stack>
      </Grid>
      <Grid item xs={12} md={4}></Grid>
    </Grid>
  );
}
